import { ApolloError } from '@apollo/client';

import {
  GetSubscriptionUpdateLinkDocument,
  GetSubscriptionUpdateLinkQuery,
  GetSubscriptionUpdateLinkQueryVariables,
  GetSubscriptionUpdatePaymentLinkDocument,
  GetSubscriptionUpdatePaymentLinkQuery,
  GetSubscriptionUpdatePaymentLinkQueryVariables,
  PreviewUpdateSubscriptionResultFieldsFragment,
  SubscriptionPeriod,
  SubscriptionPlan,
  UpdateSubscriptionDocument,
  UpdateSubscriptionMutation,
  UpdateSubscriptionMutationVariables
} from '~/api/graphql';
import { client } from '~/api/client';

export type SubscriptionCheckoutInput = {
  plan: SubscriptionPlan;
  period: SubscriptionPeriod;
  seats: number;
  voiceAgents: number;
};

export type SubscriptionCheckoutPreview = {
  checkoutUrl?: string | null;
  clientSecret?: string | null;
  invoicePreview: PreviewUpdateSubscriptionResultFieldsFragment['invoicePreview'];
  requiresExternalCheckout: boolean;
  requiresPaymentMethod: boolean;
  canChargePaymentMethodOnFile: boolean;
};

const toPreview = (
  result: PreviewUpdateSubscriptionResultFieldsFragment | null | undefined
): SubscriptionCheckoutPreview | null => {
  if (!result) {
    return null;
  }
  const requiresExternalCheckout = !!result.checkoutUrl;
  const requiresPaymentMethod = !!result.clientSecret;
  return {
    checkoutUrl: result.checkoutUrl,
    clientSecret: result.clientSecret,
    invoicePreview: result.invoicePreview,
    requiresExternalCheckout,
    requiresPaymentMethod,
    canChargePaymentMethodOnFile: !requiresExternalCheckout && !requiresPaymentMethod
  };
};

export const previewSubscriptionCheckout = async (
  input: SubscriptionCheckoutInput
): Promise<{
  preview: SubscriptionCheckoutPreview | null;
  error?: string;
}> => {
  try {
    const result = await client.query<
      GetSubscriptionUpdateLinkQuery,
      GetSubscriptionUpdateLinkQueryVariables
    >({
      query: GetSubscriptionUpdateLinkDocument,
      variables: { input },
      fetchPolicy: 'network-only'
    });
    if (result.errors?.length) {
      const graphQLError = new ApolloError({ graphQLErrors: result.errors });
      return { preview: null, error: graphQLError.message };
    }
    const preview = toPreview(result.data?.subscriptionManagementLinks?.previewUpdateSubscription);
    if (!preview) {
      return { preview: null, error: 'Empty preview' };
    }
    return { preview };
  } catch (error) {
    return { preview: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export type FinalizeCheckoutResult =
  | { status: 'updated' }
  | { status: 'external' }
  | { status: 'needs_payment_method' }
  | { status: 'error'; error?: string };

export const finalizeSubscriptionCheckout = async (
  input: SubscriptionCheckoutInput,
  preview: SubscriptionCheckoutPreview
): Promise<FinalizeCheckoutResult> => {
  if (preview.requiresExternalCheckout && preview.checkoutUrl) {
    window.open(preview.checkoutUrl, '_blank');
    return { status: 'external' };
  }
  if (preview.requiresPaymentMethod) {
    return { status: 'needs_payment_method' };
  }
  try {
    const result = await client.mutate<
      UpdateSubscriptionMutation,
      UpdateSubscriptionMutationVariables
    >({
      mutation: UpdateSubscriptionDocument,
      variables: { input }
    });
    if (result.errors?.length) {
      const graphQLError = new ApolloError({ graphQLErrors: result.errors });
      return { status: 'error', error: graphQLError.message };
    }
    if (result.data?.updateSubscription?.plan) {
      return { status: 'updated' };
    }
    return { status: 'error', error: 'Unknown error' };
  } catch (error) {
    return { status: 'error', error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const openBillingPortal = async (): Promise<{ url?: string; error?: string }> => {
  try {
    const result = await client.query<
      GetSubscriptionUpdatePaymentLinkQuery,
      GetSubscriptionUpdatePaymentLinkQueryVariables
    >({
      query: GetSubscriptionUpdatePaymentLinkDocument,
      fetchPolicy: 'network-only'
    });
    if (
      result.errors?.length ||
      !result.data?.subscriptionManagementLinks?.updatePaymentMethodUrl
    ) {
      return { error: 'Unable to open billing portal.' };
    }
    return { url: result.data.subscriptionManagementLinks.updatePaymentMethodUrl };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unable to open billing portal.'
    };
  }
};
